import React, { useState, useEffect, Suspense, lazy } from 'react';
import { useAuth } from '../context/AuthContext';
import Login from './auth/Login';
import Register from './auth/Register';
import BottomNav from '../BottomNav';
import SlideUpMenu from '../SlideUpMenu';
import HomeProfile from '../HomeProfile';
import ProfileModal from './ProfileModal';
import QRScanner from './QRScanner';

// Lazy-loaded components (heavy dependencies: recharts, leaflet, emoji-picker, xlsx)
const AdminDashboard = lazy(() => import('./admin/AdminDashboard'));
const ProjectsList = lazy(() => import('./projects/ProjectsList'));
const DeviceList = lazy(() => import('./DeviceList'));
const TrainingModule = lazy(() => import('./TrainingModule'));
const RepairsView = lazy(() => import('./repairs/RepairsView'));
const HourReportingView = lazy(() => import('./hour-reporting/HourReportingView'));
const ReportsView = lazy(() => import('./reports/ReportsView'));
const QMSView = lazy(() => import('./qms/QMSView'));
const WorksiteDiaryManagement = lazy(() => import('./admin/WorksiteDiaryManagement'));
const CRMView = lazy(() => import('./crm/CRMView'));

// Loading spinner for Suspense fallback
const LoadingSpinner = () => (
  <div style={{
    minHeight: '60vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#0f172a'
  }}>
    <div style={{ textAlign: 'center', color: '#94a3b8' }}>
      <div style={{
        width: '48px',
        height: '48px',
        border: '3px solid #334155',
        borderTop: '3px solid #FFEB3B',
        borderRadius: '50%',
        margin: '0 auto 1rem',
        animation: 'spin 0.8s linear infinite'
      }} />
      <p style={{ fontSize: '14px' }}>Ladataan...</p>
    </div>
    <style>{`
      @keyframes spin {
        to { transform: rotate(360deg); }
      }
    `}</style>
  </div>
);

// Valid views for URL hash navigation
const VALID_VIEWS = ['home', 'devices', 'training', 'qms', 'tasks', 'repairs', 'hourReporting', 'reports', 'admin', 'diary', 'crm'];

// Get initial view from URL hash
const getInitialView = () => {
  const hash = window.location.hash.replace('#', '');
  return VALID_VIEWS.includes(hash) ? hash : 'home';
};

export default function AuthContent({ showRegister, setShowRegister }) {
  const { user, loading, logout, isAdmin, isSuperAdmin, hasPermission, tenant } = useAuth();
  const [activeTab, setActiveTab] = useState(() => {
    const view = getInitialView();
    // Map view to bottom nav tab
    // Slot views should show as active in bottom nav
    const slotViews = ['qms', 'tasks', 'training', 'repairs', 'hourReporting', 'reports', 'diary'];
    if (slotViews.includes(view)) return view;
    if (view === 'admin') return 'home';
    return view;
  });
  const [currentView, setCurrentView] = useState(getInitialView);
  const [navigationOptions, setNavigationOptions] = useState({});

  // QR Scanner states
  const [showQRScanner, setShowQRScanner] = useState(false);
  const [scannedDeviceQR, setScannedDeviceQR] = useState(null);
  const [qrScanTimestamp, setQrScanTimestamp] = useState(null);

  // Menu state
  const [showMenu, setShowMenu] = useState(false);
  const [showAdmin, setShowAdmin] = useState(() => getInitialView() === 'admin');
  const [showProfileModal, setShowProfileModal] = useState(false);

  // Track if we're handling popstate to avoid double history.back()
  const isHandlingPopState = React.useRef(false);

  // Helper to close modal and clean up history
  const closeModalWithHistory = (setter) => {
    if (!isHandlingPopState.current) {
      // Closed via X button - go back in history
      window.history.back();
    }
    setter(false);
  };

  // History-aware navigation function
  const navigateToView = (view, options = {}) => {
    const { replaceState = false, skipHistory = false } = options;

    // Update URL hash
    if (!skipHistory) {
      const newUrl = view === 'home' ? window.location.pathname : `${window.location.pathname}#${view}`;
      if (replaceState) {
        window.history.replaceState({ view }, '', newUrl);
      } else {
        window.history.pushState({ view }, '', newUrl);
      }
    }

    // Update state
    setCurrentView(view);
    setShowAdmin(view === 'admin');

    // Map view to bottom nav tab
    // Views that can be in customizable slots should set their own activeTab
    const slotViews = ['qms', 'tasks', 'training', 'repairs', 'hourReporting', 'reports', 'diary', 'crm'];
    if (slotViews.includes(view)) {
      setActiveTab(view);
    } else if (['admin'].includes(view)) {
      // Admin doesn't have a bottom nav tab, keep current or set to home
      setActiveTab('home');
    } else {
      setActiveTab(view);
    }
  };

  // Handle browser back/forward buttons
  useEffect(() => {
    const handlePopState = (event) => {
      const state = event.state || {};

      // Check if we're closing a modal
      if (state.modal === 'closing') {
        // Modal was closed, nothing to do
        return;
      }

      // Check if a modal is currently open - close it instead of navigating
      if (showProfileModal) {
        isHandlingPopState.current = true;
        setShowProfileModal(false);
        isHandlingPopState.current = false;
        return;
      }
      if (showQRScanner) {
        isHandlingPopState.current = true;
        setShowQRScanner(false);
        isHandlingPopState.current = false;
        return;
      }
      // Note: Menu is just closed on any navigation, no special handling needed
      if (showMenu) {
        setShowMenu(false);
      }

      const view = state.view || window.location.hash.replace('#', '') || 'home';
      const validView = VALID_VIEWS.includes(view) ? view : 'home';

      // Update state without pushing to history
      setCurrentView(validView);
      setShowAdmin(validView === 'admin');
      setShowMenu(false);

      if (['home', 'tasks', 'repairs', 'hourReporting', 'reports', 'admin'].includes(validView)) {
        setActiveTab('home');
      } else {
        setActiveTab(validView);
      }
    };

    window.addEventListener('popstate', handlePopState);

    return () => window.removeEventListener('popstate', handlePopState);
  }, [showProfileModal, showQRScanner]);

  // Set initial history state only once on mount
  useEffect(() => {
    const initialView = getInitialView();
    window.history.replaceState({ view: initialView }, '', window.location.href);
  }, []);

  // Push history state when modals open
  useEffect(() => {
    if (showProfileModal) {
      window.history.pushState({ modal: 'profile' }, '');
    }
  }, [showProfileModal]);

  useEffect(() => {
    if (showQRScanner) {
      window.history.pushState({ modal: 'qrscanner' }, '');
    }
  }, [showQRScanner]);

  // Note: Menu doesn't push history state - it's just an overlay, not a navigation state

  // Navigation handler to support options
  const handleNavigation = (view, options = {}) => {
    navigateToView(view);
    setNavigationOptions(options);
  };

  // Check for QR code redirect (before login)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const qrCode = urlParams.get('qr');

    // Store QR code for redirect after login
    if (qrCode && !user) {
      localStorage.setItem('pending_qr_redirect', qrCode);
    }
  }, [user]);

  // Check for QR code redirect (when user is logged in)
  useEffect(() => {
    if (user && !loading) {
      const urlParams = new URLSearchParams(window.location.search);
      const qrCode = urlParams.get('qr');
      const pendingQR = localStorage.getItem('pending_qr_redirect');

      // Check URL parameter first, then localStorage
      const targetQR = qrCode || pendingQR;

      if (targetQR) {
        // User is logged in and has QR code in URL or localStorage
        // Navigate to devices view with the scanned QR code
        setScannedDeviceQR(targetQR);
        setQrScanTimestamp(Date.now()); // Required for DeviceList to process the QR
        setCurrentView('devices');
        setActiveTab('devices');

        // Update URL hash to #devices (replace state to avoid back button issues)
        window.history.replaceState({ view: 'devices' }, '', `${window.location.pathname}#devices`);

        // Clear localStorage
        localStorage.removeItem('pending_qr_redirect');
      }
    }
  }, [user, loading]);

  // Clear scanned QR code when navigating away from devices view
  useEffect(() => {
    if (currentView !== 'devices' && scannedDeviceQR) {
      setScannedDeviceQR(null);
    }
  }, [currentView, scannedDeviceQR]);

  // Handle view URL parameter (e.g., from UserDetailPage back button)
  useEffect(() => {
    if (user && !loading) {
      const urlParams = new URLSearchParams(window.location.search);
      const viewParam = urlParams.get('view');

      if (viewParam === 'admin') {
        navigateToView('admin', { replaceState: true });
      }
    }
  }, [user, loading]);

  // Handle menu item clicks
  const handleMenuItemClick = (itemId) => {
    // Close menu first
    setShowMenu(false);

    // Then handle the selected action
    switch(itemId) {
      case 'tasks':
        navigateToView('tasks');
        break;
      case 'repairs':
        navigateToView('repairs');
        break;
      case 'hourReporting':
        navigateToView('hourReporting');
        break;
      case 'reports':
        navigateToView('reports');
        break;
      case 'training':
        navigateToView('training');
        break;
      case 'qms':
        navigateToView('qms');
        break;
      case 'diary':
        navigateToView('diary');
        break;
      case 'crm':
        navigateToView('crm');
        break;
      case 'profile':
        setShowProfileModal(true);
        break;
      case 'admin':
        navigateToView('admin');
        break;
      case 'logout':
        logout();
        break;
      default:
        break;
    }
  };

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#FFEB3B'
      }}>
        <div style={{ textAlign: 'center', color: '#1e293b' }}>
          <div style={{
            width: '64px',
            height: '64px',
            border: '4px solid rgba(255,255,255,0.3)',
            borderTop: '4px solid white',
            borderRadius: '50%',
            margin: '0 auto 1rem',
            animation: 'spin 1s linear infinite'
          }} />
          <p style={{ fontSize: '18px', fontWeight: '600' }}>Loading...</p>
        </div>
        <style>{`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  if (!user) {
    return showRegister ? (
      <Register onSwitchToLogin={() => setShowRegister(false)} />
    ) : (
      <Login onSwitchToRegister={() => setShowRegister(true)} />
    );
  }

  // Render different views based on currentView state
  // All lazy-loaded components wrapped in Suspense
  const renderView = () => {
    if (showAdmin) {
      return (
        <Suspense fallback={<LoadingSpinner />}>
          <AdminDashboard onBack={() => {
            navigateToView('home');
          }} />
        </Suspense>
      );
    }

    switch(currentView) {
      case 'home':
        // New homepage - not lazy loaded (fast initial render)
        return <HomeProfile onNavigate={handleNavigation} />;

      case 'tasks':
        // Old home (ProjectsList) moved here - accessible from menu
        return (
          <Suspense fallback={<LoadingSpinner />}>
            <ProjectsList />
          </Suspense>
        );

      case 'repairs':
        // Repairs view - accessible from menu
        return (
          <Suspense fallback={<LoadingSpinner />}>
            <RepairsView />
          </Suspense>
        );

      case 'hourReporting':
        // Hour Reporting view - check feature flag
        if (!isSuperAdmin && !tenant?.feature_hour_tracking) {
          return <HomeProfile onNavigate={handleNavigation} />;
        }
        return (
          <Suspense fallback={<LoadingSpinner />}>
            <HourReportingView onClose={() => {
              navigateToView('home');
            }} />
          </Suspense>
        );

      case 'reports':
        // Reports view - accessible from menu
        return (
          <Suspense fallback={<LoadingSpinner />}>
            <ReportsView onClose={() => {
              navigateToView('home');
            }} />
          </Suspense>
        );

      case 'devices':
        return (
          <Suspense fallback={<LoadingSpinner />}>
            <DeviceList
              onClose={() => {
                setScannedDeviceQR(null);
                navigateToView('home');
                setNavigationOptions({});
              }}
              scannedQRCode={scannedDeviceQR}
              qrScanTimestamp={qrScanTimestamp}
              openAddModal={navigationOptions.openAddModal}
              showMap={navigationOptions.showMap}
            />
          </Suspense>
        );

      case 'training':
        return (
          <Suspense fallback={<LoadingSpinner />}>
            <TrainingModule
              user={user}
              showExpiring={navigationOptions.showExpiring}
              initialView={navigationOptions.initialView}
              onClose={() => {
                navigateToView('home');
                setNavigationOptions({});
              }}
            />
          </Suspense>
        );

      case 'qms':
        // QMS view - check feature flag
        if (!isSuperAdmin && !tenant?.feature_qms) {
          return <HomeProfile onNavigate={handleNavigation} />;
        }
        return (
          <Suspense fallback={<LoadingSpinner />}>
            <QMSView user={user} />
          </Suspense>
        );

      case 'diary':
        // Check feature flag and diary.view permission
        if (!isSuperAdmin && !tenant?.feature_diary) {
          return <HomeProfile onNavigate={handleNavigation} />;
        }
        if (!isAdmin && !isSuperAdmin && !hasPermission('diary.view')) {
          return <HomeProfile onNavigate={handleNavigation} />;
        }
        return (
          <Suspense fallback={<LoadingSpinner />}>
            <WorksiteDiaryManagement />
          </Suspense>
        );

      case 'crm':
        // CRM view - check feature flag
        if (!isSuperAdmin && !tenant?.feature_crm) {
          return <HomeProfile onNavigate={handleNavigation} />;
        }
        return (
          <Suspense fallback={<LoadingSpinner />}>
            <CRMView user={user} />
          </Suspense>
        );

      default:
        return <HomeProfile onNavigate={handleNavigation} />;
    }
  };

  return (
    <>
      <div style={{ position: 'relative', minHeight: '100vh' }}>
        {renderView()}

        {/* Global Bottom Nav */}
        <BottomNav
          activeTab={activeTab}
          onTabChange={(tab) => {
            if (tab === 'menu') {
              // Open slide-up menu instead of navigating
              setShowMenu(true);
            } else {
              navigateToView(tab);
            }
          }}
          onAddClick={() => {
            // Open QR Scanner when center button is clicked
            setShowQRScanner(true);
          }}
          tenant={tenant}
        />
      </div>

      {/* QR Scanner Modal - Appears above everything */}
      {showQRScanner && (
        <QRScanner
          mode="single"
          onClose={() => closeModalWithHistory(setShowQRScanner)}
          onScan={(qrCode) => {
            // Close scanner and navigate to devices in same batch
            setShowQRScanner(false);
            setScannedDeviceQR(qrCode);
            setQrScanTimestamp(Date.now());
            setCurrentView('devices');
            setActiveTab('devices');
            // Replace history state directly (avoid popstate triggering useEffect re-run)
            window.history.replaceState({ view: 'devices' }, '', `${window.location.pathname}#devices`);
          }}
        />
      )}

      {/* Slide-up Menu */}
      <SlideUpMenu
        isOpen={showMenu}
        onClose={() => setShowMenu(false)}
        onMenuItemClick={handleMenuItemClick}
        isAdmin={isAdmin}
        isSuperAdmin={isSuperAdmin}
        user={user}
        hasPermission={hasPermission}
        tenant={tenant}
      />

      {/* Profile Modal */}
      {showProfileModal && (
        <ProfileModal
          onClose={() => closeModalWithHistory(setShowProfileModal)}
          hasPermission={hasPermission}
          isAdmin={isAdmin}
          isSuperAdmin={isSuperAdmin}
        />
      )}
    </>
  );
}
